<template>
  <div
    class="rich-text"
    :class="{ skeleton: skeleton, loading: loading, expandable: canToggle }"
    @click="handleClick"
  >
    <template v-if="skeleton">
      <div class="output-text mce-content">
        <cv-skeleton-text :paragraph="true" width="100%" />
      </div>
    </template>
    <template v-if="loading">
      <inline-loader />
      <message-component>{{ loadingMessage }}</message-component>
    </template>
    <template v-else>
      <div
        ref="container"
        v-safe-html="contentRender"
        class="output-text mce-content rich-text-content"
        :class="cssClass"
      />
      <tooltip-button
        v-if="showDrawingButton"
        class="btn-make-img-copy"
        kind="ghost"
        direction="bottom"
        alignment="end"
        :disabled="false"
        :tip="t('google_attachment_mark_using_drawing')"
        icon-only
        @click="handleMakeImageCopy"
      >
        <svg-icon name="Pen24" kind="action" />
      </tooltip-button>
      <div v-if="richTextReady" class="rich-text-actions">
        <cv-link v-if="canToggle" class="toggle" @click.stop.prevent="handleToggle">
          <span v-if="isExpanded" class="non-highlightable">{{ readLessLabel }} </span>
          <span v-else class="non-highlightable">{{ readMoreLabel }} </span>
        </cv-link>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
// ─── Vue core / ecosystem ──────────────────────────────────────────────
import {
  computed,
  createApp,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { debounceFn } from "@/utils/debounce";
import DOMUtils from "@utils/dom.utils";
import RandomUtils from "@utils/random.utils";
import ScrollUtils from "@utils/scroll.utils.ts";
import { isDefined, isEmpty, isNull, notNull } from "@utils/var.check";
import htmlSanitizer from "@plugins/common/service/html.sanitizer";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import embedUtils from "../utils/embed.utils";
import RichTextUtils from "../utils/richtext.utils";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import StpUtils from "@plugins/stp/stp.utils";
import DynamicLinkUtils from "@plugins/dynamic-link/dynamic.link.util";
import StudentTooltipUtils from "@plugins/student-tooltip/student.tooltip.utils";
import { fnQ, loadWirisPlugin } from "@plugins/richtext/utils/wiris.utils.ts";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import { useDynamicMountParent } from "@plugins/common/composables/dynamic-mount-parent.ts";
import { useDynamicLinkStore } from "@plugins/dynamic-link/dynamic.link.store";
import { useNotebookEditorStore } from "@modules/notebook-editor/use-notebook-editor-store.ts";
import ROLE from "@/configs/role";
import { EXTRA_HEIGHT } from "@plugins/richtext/richtext.constant.ts";
import { CONFIG, CONTEXT } from "@plugins/google/google.constant";
import {
  ANCHOR_ADDITIONAL_OFFSET,
  ANCHORING_CONTEXT_TYPE,
  ANNOTATION_TYPE,
} from "@modules/annotation/service/annotation.constant";
import { MEDIA_TYPE, NAMESPACE } from "@modules/editor/service/editor.constant";
import { AlgebraKitEventBus } from "@plugins/richtext/service/algebrakit.event.bus";
import { AnchoringEventBus } from "@/modules/annotation/service/anchoring.event.bus";
import { MediaPlayerEventBus } from "@plugins/media/service/media.player.event.bus";
import drawingEventBus from "@plugins/richtext/plugins/drawing/service/event-bus";
import userActivityTrackEvent from "@plugins/user-activity-track/service/user.activity.track.event.ts";
import algebraKitApi from "@/apis/algebraKit.api.ts";
import store from "@plugins/store";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import InlineLoader from "@plugins/common/components/InlineLoader.vue";
import MessageComponent from "@plugins/common/components/MessageComponent.vue";
import RichTextTooltip from "./RichTextTooltip.vue";
import StpToolTip from "@plugins/stp/components/StpTooltip.vue";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import TextToSpeech from "@plugins/richtext/plugins/text-to-speech/components/TextToSpeech.vue";
import SpeechEvaluation from "@plugins/richtext/plugins/speech-evaluation/SpeechEvaluation.vue";
import GoogleAttachment from "@plugins/google/components/GoogleAttachment.vue";
import GoogleDocumentStore from "@plugins/google/components/GoogleDocumentStore";
import AlgebraKit from "@plugins/richtext/plugins/algebra-kit/AlgebraKit.vue";
import AlgebraKitContentView from "@plugins/richtext/plugins/algebra-kit/AlgebraKitContentView.vue";
import DrawingCanvasModal from "@plugins/drawing/components/DrawingCanvasModal.vue";
import StudentTooltip from "@plugins/student-tooltip/components/StudentTooltip.vue";
import SlsLabelComponent from "@plugins/richtext/plugins/label/components/SlsLabelComponent.vue";
import SlsLabelUtils from "@plugins/richtext/plugins/label/utils/sls.label.utils.ts";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import { Annotation } from "@modules/annotation/types.ts";
import { AnnotationContext, CanvasConfig, Unwatch } from "@plugins/richtext/types.ts";
import { ContextMessage } from "@/modules/conversation/types";
import { AnnotationEventBus } from "@modules/annotation/service/annotation.event.bus";

const pendingTasks: (() => void)[] = [];
let instance = null;

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    loadingMessage?: string;
    disabled?: boolean;
    visible?: boolean; // Hidden/displayed upon initial rendering
    expandable?: boolean; // Allowed to be expanded/collapsed if possible
    expanded?: boolean; // Initial state
    skeleton?: boolean;
    truncate?: boolean; // To force content truncation
    imageRescale?: boolean; // to rescale image
    content?: string;
    annotatable?: boolean;
    canMarkCopy?: boolean;
    showMediaOnly?: boolean;
    // Primary - pale blue background truncation
    // Secondary - white background truncation
    truncationType?: string;
    copyrightProtected?: boolean;
    lazyload?: boolean;
    config?: Record<string, unknown>;
    questionAttemptUuids?: string[];
    pageId?: number | null;
    isOwnView?: boolean;
    isStudentView?: boolean;
    componentType?: string;
    showDrawingButton?: boolean;
    isComment?: boolean;
    timeStamp?: number | null;
    hasSEAudio?: boolean;
    isSEGraded?: boolean;
    showComponentOverlay?: boolean;
    questionAttemptUuid?: string;
    commentContext?: Record<string, unknown> | undefined | null;
    enableAnnotationScroll?: boolean;
    loadFirstMedia?: boolean;
    showDrawingRestriction?: boolean;
    anchor?: string;
    showCaption?: boolean;
    canAskSalis?: boolean;
    isAskSalisSupportedComponent?: boolean;
    showMediaTrash?: boolean;
    disableMedia?: boolean;
    showMediaSettings?: boolean | null;
    showMediaDownload?: boolean | null;
  }>(),
  {
    loading: false,
    loadingMessage: "",
    disabled: false,
    visible: true,
    expandable: true,
    expanded: true,
    skeleton: false,
    truncate: false,
    imageRescale: true,
    content: "",
    annotatable: false,
    canMarkCopy: false,
    showMediaOnly: false,
    truncationType: "primary",
    copyrightProtected: false,
    lazyload: true,
    config: () => ({}),
    questionAttemptUuids: () => [],
    pageId: null,
    isOwnView: undefined,
    isStudentView: undefined,
    componentType: "",
    showDrawingButton: undefined,
    isComment: false,
    timeStamp: null,
    hasSEAudio: false,
    isSEGraded: false,
    showComponentOverlay: false,
    questionAttemptUuid: "",
    commentContext: undefined,
    enableAnnotationScroll: false,
    loadFirstMedia: false,
    showDrawingRestriction: false,
    anchor: "",
    showCaption: true,
    canAskSalis: false,
    isAskSalisSupportedComponent: false,
    showMediaTrash: false,
    disableMedia: false,
    showMediaSettings: null,
    showMediaDownload: null,
  },
);

const emit = defineEmits<{
  (e: "hasMedia"): void;
  (e: "doubleClickImage"): void;
  (e: "markGoogleFile", data: unknown): void;
  (e: "navigateToQuestion", value: unknown): void;
  (e: "clickImage", params: unknown): void;
  (e: "isModeChanged", params: unknown): void;
  (e: "focus"): void;
  (e: "ready"): void;
  (e: "handleAnchorAnnotation", index: string): void;
  (e: "handleMakeImageCopy"): void;
  (e: "timestampTrigger", params: unknown): void;
  (e: "googleAttachmentClicked", data: unknown): void;
  (e: "markCopy", data: unknown): void;
  (e: "mediaRemoved"): void;
  (e: "mediaSettings", settings: unknown): void;
  (e: "editEmbed", settings: unknown): void;
}>();

const printView = inject<boolean>("printView", false);
const richTextContext = inject("richTextContext", {});
const annotationContext = inject<AnnotationContext>("annotationContext", {
  isOwnView: false,
  isPreview: false,
  isActivityTeamResponse: false,
  isTeacher: false,
  isPaginated: false,
  isAssignmentPaused: () => false,
  isGameEnabled: () => false,
  isLinkedAssignment: () => false,
});
const questionContext = inject("questionContext", {});
const pageContext = inject("pageContext", {});
const questionAttemptContext = inject("questionAttemptContext", {});
const resourceContext = inject("resourceContext", {});
const componentContext = inject("componentContext", {});

const { t } = useI18n();
const route = useRoute();
const modal = useModal();
const storeVuex = useStore();
const containerRef = useTemplateRef("container");
const parentExposed = ref<Record<string, unknown> | null>(null);

const isExpandable = ref<boolean>(false);
const isExpanded = ref<boolean>(false); // Default to false so as to ensure expandability is calculated based on fixed height first
const richTextReady = ref<boolean>(false);
const richTextPluginReadyMap = ref<Record<string, boolean>>({});
const richTextLoadedLimited = ref<boolean>(false);
const richTextPluginLoadedLimitedMap = ref<Record<string, boolean>>({});
const WirisInterval = ref<number | null>(null);
const richTextMedias = ref([]);
const googleAttachments = ref([]);
const dynamicLinkWatchInstances = ref<(() => void)[]>([]);
const dynamicLinkStore = useDynamicLinkStore();
const googleDocumentStore = GoogleDocumentStore;
const whitelistHtmlFetched = ref<boolean>(false);
const drawModalId = ref<string | number | null>(null);
const isDestroyed = ref<boolean>(false);
const selectionListenersResumeTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const isCheckingExpandability = ref<boolean>(true);

const getWhitelistHtmlTags = computed(
  () => storeVuex.getters[`${NAMESPACE.APP}/getWhitelistHtmlTags`],
);

const cssClass = computed(() => {
  const isCollapsed = !isExpanded.value;
  // collapsed can only be added after updateIsExpanded is called aka when richtext is ready
  return [
    {
      "expandability-check": isCheckingExpandability.value && props.expandable,
      collapsed: richTextReady.value && canToggle.value && isCollapsed,
      truncate: props.truncate,
      loaded: richTextReady.value || richTextLoadedLimited.value,
      disabled: props.disabled,
      "show-component-overlay": props.showComponentOverlay,
      "has-overflow-menu-button": props.showDrawingButton,
    },
    `${props.truncationType}-truncation`,
  ];
});

const readMoreLabel = computed(() => t("read_more"));
const readLessLabel = computed(() => t("read_less"));
const canToggle = computed(() => props.expandable && isExpandable.value);

const sanitizedContent = computed(() => {
  const html = htmlSanitizer.sanitizeHtmlForRichText(props.content, getWhitelistHtmlTags.value);
  return props.loadFirstMedia ? RichTextUtils.replaceUgcMediaAfterFirst(html) : html;
});

const contentRender = computed(() => {
  if (props.showMediaOnly) {
    const tmp = DOMUtils.createElement("div");
    tmp.innerHTML = sanitizedContent.value;
    const media = DOMUtils.elOf(tmp, ".sls-media");
    return media[0]?.outerHTML ?? "";
  }
  return sanitizedContent.value;
});

function processWiris(el: HTMLElement, parent) {
  const pluginName = "wiris";
  const checkWirisLoaded = () => {
    if (
      window.com &&
      window.com.wiris &&
      window.com.wiris.js &&
      window.com.wiris.js.JsPluginViewer
    ) {
      window.com.wiris.js.JsPluginViewer.parseElement(el, true);
      if (parent.WirisInterval) clearInterval(parent.WirisInterval);

      parent.WirisInterval = setInterval(() => {
        const mathMLList = DOMUtils.elOf(el, "math");
        if (mathMLList.length === 0) {
          clearInterval(parent.WirisInterval);
          parent.updatePluginReadyState(pluginName);
          return;
        }
      }, 300) as unknown as number;
    } else {
      fnQ.push(checkWirisLoaded);
    }
  };
  loadWirisPlugin(checkWirisLoaded);
}

function processTooltips(el: HTMLElement, parent) {
  const pluginName = "tooltip";
  const selector = ".custom-tooltip";
  DOMUtils.elOf(el, selector).forEach((node: Element, index: number) => {
    const element = node as HTMLElement;
    const id = `${pluginName}_${index}`;
    // eslint-disable-next-line vue/one-component-per-file
    const richTextTooltipApp = createApp(RichTextTooltip, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      context: parent.richTextContext,
      label: element.innerHTML,
      tip: element.getAttribute("title"),
      id: id,
      onLoaded: parent.tooltipLoaded,
    });
    AppInstanceUtils.installCommonPlugin(richTextTooltipApp);
    parent.addPluginReadyState(id);
    // Mount media component to media element, include the media tag
    const component = richTextTooltipApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);

    // process MO inside tooltip
    const mediaSelector = ".sls-media, .sls-media-object, .sls-external-media";
    if (DOMUtils.elOf(component.$el, mediaSelector).length !== 0) {
      processMediaObjects(component.$el, parent);
    }
  });
}

function processSlsLabel(el: HTMLElement, parent) {
  const selector = SlsLabelUtils.SELECTOR;
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const data = SlsLabelUtils.retrieveDataFromElement(element);
    // eslint-disable-next-line vue/one-component-per-file
    const SlsLabelComponentApp = createApp(SlsLabelComponent, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      kind: data.kind,
      textLabelKey: data.textLabelKey,
      classes: data.classes,
    });
    AppInstanceUtils.installCommonPlugin(SlsLabelComponentApp);
    // Mount media component to media element, include the media tag
    const component = SlsLabelComponentApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function processStpTooltips(el: HTMLElement, parent) {
  const pluginName = "stp";
  const selector = ".teacher-stp";
  if (parent.richTextContext && parent.richTextContext.viewingRole !== ROLE.TEACHER) {
    DOMUtils.elOf(el, selector).forEach((node: Element) => {
      const element = node as HTMLElement;
      element.remove();
    });
    return;
  }
  DOMUtils.elOf(el, selector).forEach((node: Element, index: number) => {
    const element = node as HTMLElement;
    const id = `${pluginName}_${index}`;
    const data = StpUtils.retrieveDataFromStpTooltip(element);
    // eslint-disable-next-line vue/one-component-per-file
    const StpToolTipApp = createApp(StpToolTip, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      stpData: data,
      showTooltip: true,
      id: id,
      onLoaded: parent.tooltipLoaded,
    });
    AppInstanceUtils.installCommonPlugin(StpToolTipApp);
    parent.addPluginReadyState(id);
    const component = StpToolTipApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function processStudentTooltips(el: HTMLElement, parent) {
  const selector = ".student-tooltip";
  DOMUtils.elOf(el, selector).forEach((node: Element, index: number) => {
    const element = node as HTMLElement;
    const id = `TOOLTIP_${index}`;
    const data = StudentTooltipUtils.retrieveDataFromStudentTooltip(element);

    // eslint-disable-next-line vue/one-component-per-file
    const RichTextStudentTooltipApp = createApp(StudentTooltip, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      tooltipData: data,
      showTooltip: true,
      id: id,
      onLoaded: parent.tooltipLoaded,
      onHandleAnchorAnnotation: parent.handleAnchorAnnotation,
    });
    AppInstanceUtils.installCommonPlugin(RichTextStudentTooltipApp);
    parent.addPluginReadyState(id);
    const component = RichTextStudentTooltipApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function processYouTubes(el: HTMLElement, parent) {
  const selector = ".sls-youtube";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const uuid = RandomUtils.randomUuid();
    parent.addPluginReadyState(uuid);
    // eslint-disable-next-line vue/one-component-per-file
    const mediaApp = createApp(MediaComponent, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      uuid: uuid,
      context: parent.richTextContext,
      mediatype: "youtube",
      lazyload: parent.lazyload,
      youtubeSrc: element.getAttribute("data-baseurl"),
      cuePoints: {
        start: parseInt(String(element.getAttribute("data-start"))),
        end: parseInt(String(element.getAttribute("data-end"))),
      },
      strictMode: RichTextUtils.getValueOrDefault(
        element.getAttribute("data-strict") === "true",
        false,
      ),
      printView: parent.printView,
      showTitle: false,
      onMediaLoaded: parent.mediaLoaded,
    });
    AppInstanceUtils.installCommonPlugin(mediaApp);
    // Mount media component to media element, include the media tag
    const mediaComponent = mediaApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
    parent.richTextMedias.push(mediaComponent);
  });
}

function processSpeechEvaluation(el: HTMLElement, parent) {
  const selector = ".sls-speech-evaluation";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const speechEvaluationText = element.innerHTML;
    // eslint-disable-next-line vue/one-component-per-file
    const SpeechEvaluationApp = createApp(SpeechEvaluation, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      speechEvaluationText: speechEvaluationText,
    });
    AppInstanceUtils.installCommonPlugin(SpeechEvaluationApp);
    const component = SpeechEvaluationApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function renderAlgebraKit(parent, element: HTMLElement, sessionId: string | null) {
  let spec = parent.config?.spec;
  if (isEmpty(spec)) {
    if (!isEmpty(element.getAttribute("data-spec"))) {
      spec = JSON.parse(String(element.getAttribute("data-spec")).replaceAll("##", "$$$"));
    }
  }
  const showHintAndFeedback = element.getAttribute("data-show-feedback") === "true";
  const hasRandomised = element.getAttribute("data-has-randomised") === "true";
  const isScorable = parent.config.isScorable;
  let refId = parent.config.refId;
  const hideRandomisedBanner = parent.config.hideRandomisedBanner;

  if (isEmpty(refId)) {
    if (isScorable) {
      refId = spec.definition.elements[0].interactions[0].id;
    } else {
      refId = spec.definition.elements[0].id;
    }
  }

  // eslint-disable-next-line vue/one-component-per-file
  const AlgebraKitApp = createApp(AlgebraKit, {
    ...AppInstanceUtils.generateDynamicMountProps(parent),
    sessionId: sessionId,
    refId: refId,
    solutionMode: parent.config.solutionMode ? parent.config.solutionMode : false,
    reviewMode: parent.config.reviewMode ? parent.config.reviewMode : false,
    assessmentMode: !showHintAndFeedback,
    hasRandomised: hasRandomised,
    isScorable: isScorable,
    hideRandomisedBanner: hideRandomisedBanner,
  });
  AppInstanceUtils.installCommonPlugin(AlgebraKitApp);
  const component = AlgebraKitApp.mount(element);
  DOMUtils.replaceComponentWrapperWithComponentElement(component);
}

function processAlgebraKit(el: HTMLElement, parent) {
  if (el.querySelector(".algebra-kit-component")) return;
  const selector = ".sls-algebra-kit";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    let spec = parent.config?.spec;
    if (isEmpty(spec)) {
      if (!isEmpty(element.getAttribute("data-spec"))) {
        spec = JSON.parse(String(element.getAttribute("data-spec")).replaceAll("##", "$$$"));
      }
    }

    if (!isEmpty(spec)) {
      const questionUuid = parent.config?.questionUuid;
      let sessionId = parent.config?.sessionId as string | null;
      const createNewSessionFromExistingSession =
        parent.config?.createNewSessionFromExistingSession;
      const loadError = parent.config?.loadError;
      const showFeedback = element.getAttribute("data-show-feedback") === "true";

      if (loadError) {
        sessionId = null;
        renderAlgebraKit(parent, element, sessionId);
        return;
      }

      if (isEmpty(sessionId)) {
        sessionId = null;
      }

      //if sessionId exist
      if (!isEmpty(sessionId) && !createNewSessionFromExistingSession) {
        renderAlgebraKit(parent, element, sessionId);
        return;
      }

      const exercise: Record<string, unknown> = { exerciseSpec: spec };
      if (createNewSessionFromExistingSession) {
        exercise.sessionId = sessionId;
      }

      const requestData = { exercises: [exercise], assessmentMode: !showFeedback };

      algebraKitApi
        .createSession(JSON.stringify(requestData))
        .then((response) => {
          sessionId = response[0][0].sessionId;

          if (!createNewSessionFromExistingSession) {
            AlgebraKitEventBus.$emit("createSession", {
              questionUuid: questionUuid,
              sessionId: sessionId,
            });
          }

          renderAlgebraKit(parent, element, sessionId);
        })
        .catch((error: unknown) => {
          console.log(error);

          sessionId = null;
          AlgebraKitEventBus.$emit("loadError", {
            questionUuid: questionUuid,
            sessionId: sessionId,
            loadError: true,
          });

          renderAlgebraKit(parent, element, sessionId);
        });
    } else {
      console.error("Unable to resolve algebra-kit data");
    }
  });
}

function renderAlgebraKitContentView(element: HTMLElement, dataSpec: string, parent) {
  // eslint-disable-next-line vue/one-component-per-file
  const AlgebraKitContentViewApp = createApp(AlgebraKitContentView, {
    ...AppInstanceUtils.generateDynamicMountProps(parent),
    dataSpec: dataSpec.replaceAll("##", "$$$"),
  });
  AppInstanceUtils.installCommonPlugin(AlgebraKitContentViewApp);
  const component = AlgebraKitContentViewApp.mount(element);
  DOMUtils.replaceComponentWrapperWithComponentElement(component);
}

function processAlgebraKitAnnotation(el: HTMLElement, parent) {
  const selector = ".sls-algebra-kit-content";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    if (!isEmpty(element.getAttribute("data-spec"))) {
      const dataSpec = String(element.getAttribute("data-spec"));
      renderAlgebraKitContentView(element, dataSpec, parent);
    } else {
      console.error("Unable to resolve algebra-kit data");
    }
  });
}

function processTTS(el: HTMLElement, parent) {
  const selector = ".sls-tts";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const uuid = element.getAttribute("data-uuid");
    const text = element.innerHTML;

    // eslint-disable-next-line vue/one-component-per-file
    const TextToSpeechApp = createApp(TextToSpeech, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      context: parent.richTextContext,
      audioUuid: uuid,
      audioText: text,
      onMediaLoaded: parent.mediaLoaded,
    });
    AppInstanceUtils.installCommonPlugin(TextToSpeechApp);
    const component = TextToSpeechApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(component);
  });
}

function processMediaObjects(el: HTMLElement, parent) {
  RichTextUtils.convertLegacyMediaObjects(el);
  RichTextUtils.convertLegacyImageMediaObjects(el);
  RichTextUtils.convertLegacyExternalResource(el);
  const selector = ".sls-media, .sls-media-object, .sls-external-media";
  DOMUtils.elOf(el, selector).forEach((node: Element, index: number) => {
    // if MO is inside tooltip, wait for processTooltips first before processing MO,
    // otherwise MO component will be replaced
    const element = node as HTMLElement;
    if (DOMUtils.hasClass(element.parentElement as HTMLElement, "custom-tooltip")) {
      return;
    }

    if (DOMUtils.hasClass(element, "sls-tts")) {
      processTTS(el, parent);
      return;
    }
    const mediaUuid = element.getAttribute("data-uuid") ?? "";
    const dataType = element.getAttribute("data-type") ?? "";
    const anchorIndex = `MO_${index}`;
    const moIdentifier = `${mediaUuid}_${anchorIndex}`;
    parent.addPluginReadyState(moIdentifier);

    const propsData = {
      uuid: mediaUuid,
      width: element.getAttribute("data-width"),
      height: element.getAttribute("data-height"),
      cuePoints: {
        start: element.getAttribute("data-cue-start"),
        end: element.getAttribute("data-cue-end"),
      },
      isTranscriptEnabled: RichTextUtils.getValueOrDefault(
        element.getAttribute("data-transcript-enabled") === "true",
        false,
      ),
      transcriptionFileVersion: element.getAttribute("transcription-file-version"),
      dataDisplayStart: element.getAttribute("data-display-start"),
      copyrightProtected: parent.copyrightProtected,
      lazyload: parent.lazyload,
      index: anchorIndex,
      context: parent.richTextContext,
      showTrashOverride: parent.showMediaTrash,
      disableMedia: parent.disableMedia,
      showSettingsOverride: parent.showMediaSettings,
      showDownloadOverride: parent.showMediaDownload,
      onRemove: parent.showMediaTrash ? () => emit("mediaRemoved") : undefined,
      showUnapprovedMOError: true,
      imageRescale: parent.imageRescale,
      printView: parent.printView,
      strictMode: RichTextUtils.getValueOrDefault(
        element.getAttribute("data-strict") === "true",
        false,
      ),
      canMarkCopy: parent.canMarkCopy,
      isComment: parent.isComment,
      parentAnchor:
        DOMUtils.parent(element, ".annotatable-richtext") == null
          ? ""
          : (DOMUtils.parent(element, ".annotatable-richtext") as HTMLElement).id,
      hasSEAudio: parent.hasSEAudio,
      isSEGraded: parent.isSEGraded,
      parentQuestionContext: parent.questionContext,
      commentContext: parent.commentContext,
      showDrawingRestriction: parent.showDrawingRestriction,
      caption: props.showCaption,
      canAskSalis: props.canAskSalis,
      isAskSalisSupportedComponent: props.isAskSalisSupportedComponent,
    };

    if (DOMUtils.hasClass(element, "sls-media-object")) {
      // legacy media object structure (sls-media-object)
      propsData.mediatype = (dataType || "").toLowerCase().trim();
      propsData.type = "mo";
    } else {
      // new media structure (sls-media), ugc or mo
      let mediaType = element.getAttribute("media-type") ?? "";
      if (notNull(propsData.dataDisplayStart)) {
        mediaType = MEDIA_TYPE.IMAGE;
      }
      propsData.mediatype = mediaType.toLowerCase().trim();
      propsData.fileName = element.getAttribute("data-file-name");
      propsData.isUgc = dataType === "ugc";
      propsData.type = dataType;
      propsData.altText = element.getAttribute("data-title");
      propsData.mediaSrc = element.getAttribute("data-url");
      propsData.isExternal = dataType === "external";
      propsData.zoomTools = true;
      propsData.showTitle = mediaType.toUpperCase() !== "IMAGE";
      propsData.renderTranscript = propsData.isTranscriptEnabled;
    }

    propsData.annotatable =
      parent.annotatable &&
      propsData.mediatype &&
      (propsData.mediatype.toUpperCase() === "IMAGE" ||
        propsData.mediatype.toUpperCase() === "VIDEO" ||
        propsData.mediatype.toUpperCase() === "AUDIO");

    //FOR ITT to indicate got Attachment
    emit("hasMedia");

    // eslint-disable-next-line vue/one-component-per-file
    const mediaApp = createApp(MediaComponent, {
      ...propsData,
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      onMediaLoaded: parent.mediaLoaded,
      onMediaLoadedLimited: parent.mediaLoadedLimited,
      onClickImage: parent.clickImage,
      onIsModeChanged: parent.isModeChanged,
      onDoubleClickImage: () => emit("doubleClickImage"),
      onOpenCanvasDrawing: parent.openCanvasDrawing,
      onMediaSettings: parent.handleMediaSettings,
      html5InteractiveConfig: parent.config?.html5InteractiveConfig,
      onTimestampTrigger: parent.timestampTrigger,
      questionAttemptUuid: parent.questionAttemptUuid,
    });
    AppInstanceUtils.installCommonPlugin(mediaApp);
    mediaApp.provide("annotationContext", parent.annotationContext);
    mediaApp.provide("questionContext", parent.questionContext);
    mediaApp.provide("resourceContext", parent.resourceContext);
    mediaApp.provide("componentContext", parent.componentContext);

    // Mount media component to media element, include the media tag
    const mediaComponent = mediaApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
    parent.richTextMedias.push(mediaComponent);
  });
}

function replaceGoogleAttachment({
  oldDocumentId,
  newDocumentMetadata,
}: {
  oldDocumentId: string;
  newDocumentMetadata;
}) {
  const existingGoogleComponent = (
    document.querySelector(`.google-attachment-tag[data-id='${oldDocumentId}']`) as HTMLElement
  ).parentNode as HTMLElement;

  const newGoogleAttachmentElement = DOMUtils.createElement("div", {
    class: "google-attachment-inline-placeholder",
    "data-id": newDocumentMetadata.id,
    "data-type": CONFIG.ADD,
    "data-context": CONTEXT.ATTEMPT,
    "data-processStatus": "processed",
  });

  existingGoogleComponent.innerHTML = newGoogleAttachmentElement.outerHTML;
}

function renderInlineGoogleAttachment(el: HTMLElement, parent, newDocumentId: string) {
  DOMUtils.elOf(el, ".google-attachment-inline-placeholder").forEach((node: Element) => {
    richTextUtils.encloseElementWithWrapper(node, {
      class: "google-attachment-wrapper",
      contenteditable: false,
    });

    // eslint-disable-next-line vue/one-component-per-file
    const GoogleAttachmentApp = createApp(GoogleAttachment, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      id: newDocumentId,
      width: node.getAttribute("data-width"),
      height: node.getAttribute("data-height"),
      lazyload: parent.lazyload,
      context: parent.richTextContext,
      resourceStatus: parent.pageContext.lessonStatus,
      dataType: node.getAttribute("data-type"),
      dataContext: node.getAttribute("data-context"),
      usage: node.getAttribute("data-usage"),
      config:
        isDefined(parent.config) &&
        isDefined(parent.config.plugins) &&
        isDefined(parent.config.plugins.google)
          ? parent.config.plugins.google.googleServiceConfig
          : {},
      mode: "view",
      questionAttemptUuids: parent.questionAttemptUuids,
      componentType: parent.componentType,
      isComment: parent.isComment,
      processStatus: "processed",
    });

    const attachment = richTextUtils.createInlineGoogleAttachmentTag(node);
    DOMUtils.removeClass(attachment, "google-attachment");
    DOMUtils.addClass(attachment, "google-attachment-tag");

    AppInstanceUtils.installCommonPlugin(GoogleAttachmentApp);
    const googleAttachment = GoogleAttachmentApp.mount(node);
    DOMUtils.replaceComponentWrapperWithComponentElement(googleAttachment);
    googleAttachment.$el.parentNode.appendChild(attachment);
  });
}

function processGoogleAttachments(el: HTMLElement, parent) {
  const selector = ".google-attachment";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const id = element.getAttribute("data-id") ?? "";

    if (isEmpty(parent.richTextContext.pageId)) {
      parent.richTextContext.pageId = parent.pageId;
      if (parent.printView) {
        parent.richTextContext.printView = true;
        parent.richTextContext.academicYear = props.config?.academicYear;
      }
    }

    richTextUtils.encloseElementWithWrapper(element, {
      class: "google-attachment-wrapper",
      contenteditable: false,
    });

    // eslint-disable-next-line vue/one-component-per-file
    const GoogleAttachmentApp = createApp(GoogleAttachment, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      id: id,
      width: element.getAttribute("data-width"),
      height: element.getAttribute("data-height"),
      lazyload: parent.lazyload,
      context: parent.richTextContext,
      resourceStatus: parent.pageContext.lessonStatus,
      dataType: element.getAttribute("data-type"),
      dataContext: element.getAttribute("data-context"),
      usage: element.getAttribute("data-usage"),
      config:
        isDefined(parent.config) &&
        isDefined(parent.config.plugins) &&
        isDefined(parent.config.plugins.google)
          ? parent.config.plugins.google.googleServiceConfig
          : {},
      mode: "view",
      questionAttemptUuids: parent.questionAttemptUuids,
      componentType: parent.componentType,
      questionAttemptContext: parent.questionAttemptContext,
      isComment: parent.isComment,
      onMarkGoogleFile: (data: unknown) => {
        emit("markGoogleFile", data);
      },
      onNavigateToQuestion: (value: unknown) => {
        emit("navigateToQuestion", value);
      },
      onDuplicationFromTeamMemberCompleted: ({ oldDocumentId, newDocumentMetadata }) => {
        replaceGoogleAttachment({ oldDocumentId, newDocumentMetadata });
        renderInlineGoogleAttachment(el, parent, newDocumentMetadata.id);
        parent.googleDocumentStore.removeGoogleDocument(CONTEXT.LESSON, oldDocumentId);
      },
      onGoogleAttachmentClicked: (data: unknown) => {
        emit("googleAttachmentClicked", data);
      },
    });

    const data = richTextUtils.getAttachmentDataForTag(element);
    const attachment = richTextUtils.createInlineGoogleAttachmentTag(data);
    DOMUtils.removeClass(attachment, "google-attachment");
    DOMUtils.addClass(attachment, "google-attachment-tag");

    AppInstanceUtils.installCommonPlugin(GoogleAttachmentApp);
    const googleAttachment = GoogleAttachmentApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(googleAttachment);
    googleAttachment.$el.parentNode.appendChild(attachment);
    parent.googleAttachments.push(googleAttachment);
  });
}

function processEmbedWebsite(el: HTMLElement, parent) {
  const selector = ".embed-website-wrapper";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const mediaFrame = element.querySelectorAll(".media-component");
    mediaFrame.forEach((frame) => frame.remove());
    const slsFrame = element.querySelector("div.sls-frame,sls-frame") as HTMLElement | null;
    if (!slsFrame) return;

    const frameAttributes = embedUtils.getValidFrameAttributes(slsFrame.attributes);

    let type = "";
    let embedUrl = "";
    const context = parent.richTextContext;
    const isLegacySlsFrame =
      isDefined(frameAttributes["src"]) && !isDefined(frameAttributes["websiteuuid"]);

    context.resourceUuid = isEmpty(context.resourceUuid)
      ? frameAttributes.appuuid
      : context.resourceUuid;

    if (DOMUtils.hasClass(slsFrame, "embed-url")) {
      type = "embed-url";
      if (isLegacySlsFrame) {
        embedUrl = frameAttributes.src;
      }
    } else if (DOMUtils.hasClass(slsFrame, "embed-code")) {
      type = "embed-code";
      if (isLegacySlsFrame) {
        embedUrl = frameAttributes.src;
      }
    } else {
      type = "embed-app";

      const appurl = frameAttributes.appurl ?? "";
      const id = frameAttributes.id ?? "";
      const appuuid = frameAttributes.appuuid ?? "";
      const custom = frameAttributes.custom ?? "";
      if (appuuid) {
        embedUrl = embedUtils.constructAppUrl({ id, appurl, appuuid, custom }, context);
      }
      frameAttributes.src = embedUrl;
    }

    const uuid = RandomUtils.randomUuid();
    parent.addPluginReadyState(uuid);

    // eslint-disable-next-line vue/one-component-per-file
    const mediaApp = createApp(MediaComponent, {
      ...AppInstanceUtils.generateDynamicMountProps(parent),
      uuid: uuid,
      embedConfig: { type, frameAttributes },
      lazyload: parent.lazyload,
      context: context,
      printView: parent.printView,
      showTrashOverride: parent.showMediaTrash,
      disableMedia: parent.disableMedia,
      showSettingsOverride: parent.showMediaSettings,
      showDownloadOverride: parent.showMediaDownload,
      onRemove: parent.showMediaTrash ? () => emit("mediaRemoved") : undefined,
      onMediaSettings: parent.handleMediaSettings,
      onEditEmbed: parent.openSettings,
      onMediaLoaded: (data: unknown) => {
        const whitelistDomainMaps = context.whitelistDomainMaps();
        if (DOMUtils.hasClass(slsFrame, "embed-url") || DOMUtils.hasClass(slsFrame, "embed-code")) {
          const websiteuuid = frameAttributes.websiteuuid ?? "";
          embedUrl = isLegacySlsFrame
            ? (frameAttributes.src ?? "")
            : (whitelistDomainMaps[websiteuuid] ?? "");
        }

        userActivityTrackEvent.embed({
          embedType: type,
          embedUrl: !isNull(embedUrl) ? embedUrl : "",
          lessonId: !isEmpty(parent.pageContext) ? parent.pageContext.lessonId : "",
          lessonResourceDomain: !isEmpty(parent.pageContext)
            ? parent.pageContext.lessonResourceDomain
            : "",
          // Pass appUuid only when embed type is "embed-app"
          appUuid: type === "embed-app" ? frameAttributes.appuuid || undefined : undefined,
        });
        parent.mediaLoaded(data);
      },
    });
    AppInstanceUtils.installCommonPlugin(mediaApp);
    // Mount media component to media element, include the media tag
    const mediaComponent = mediaApp.mount(element);
    DOMUtils.replaceComponentWrapperWithComponentElement(mediaComponent);
    parent.richTextMedias.push(mediaComponent);
  });
}

function processTable(el: HTMLElement) {
  // add wrapper with overflow styling for tinymce table
  const selector = "table";
  DOMUtils.elOf(el, selector).forEach((node: Element) => {
    const element = node as HTMLElement;
    const tableWrapper = DOMUtils.createElement("div", { class: "table-wrapper" });
    if (element.clientWidth > 680) {
      DOMUtils.addClass(tableWrapper, "rescaled");
    }
    DOMUtils.insertAfter(tableWrapper, element);
    const next = DOMUtils.nextElementByClass(element, "table-wrapper");
    if (next) {
      next.appendChild(element);
    }
  });
}

function processMention() {
  const mentionDivs = document.querySelectorAll(".sls-mention");

  mentionDivs.forEach((mentionDiv) => {
    // Extract the data-content attribute
    const dataContent = mentionDiv.getAttribute("data-content");

    if (dataContent) {
      // Create a new div and set its innerHTML to the extracted content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = dataContent;
      // Replace the mentionDiv with the extracted content
      mentionDiv.replaceWith(...Array.from(tempDiv.childNodes));
    }
  });
}

const fetchLessonAnchor = debounceFn(
  function (parent) {
    return new Promise((resolve) => {
      parent.dynamicLinkStore
        .fetchLessonAnchor(parent.richTextContext.lessonUuid)
        .then(resolve)
        .catch(parent.passiveApiFailHandler);
    });
  },
  500,
  { leading: true, trailing: false },
);

const fetchNotebookAnchor = debounceFn(
  function (parent) {
    return new Promise((resolve) => {
      parent.dynamicLinkStore
        .fetchNotebookAnchor(parent.richTextContext.notebookUuid)
        .then(resolve)
        .catch(parent.passiveApiFailHandler);
    });
  },
  500,
  { leading: true, trailing: false },
);

function processDynamicLink(el: HTMLElement, parent) {
  if (
    parent.richTextContext &&
    parent.richTextContext.notebookUuid &&
    !isEmpty(parent.richTextContext.notebookUuid)
  ) {
    // within notebook
    processNotebookDynamicLink(el, parent);
  } else {
    // within lesson
    processLessonDynamicLink(el, parent);
  }
}

function processLessonDynamicLink(el: HTMLElement, parent) {
  const linkTags = DynamicLinkUtils.getLinkTagsFromContainer(el);
  if (!linkTags?.length) {
    // stop here since no dynamic link is found
    return;
  }
  // executes in parallel, let DynamicInternalLinks update reactively
  fetchLessonAnchor(parent).then(() => {
    // get new lesson anchor when new activity is added
    if (store.state.editor?.lesson?.activityById) {
      const activityWatcher = debounceFn(
        function () {
          parent.dynamicLinkStore.refreshLessonAnchor();
        },
        500,
        { leading: true, trailing: false },
      );
      const unwatch = store.watch(
        (state) => {
          return state.editor?.lesson?.activityById;
        },
        () => activityWatcher(),
        { deep: true },
      );
      parent.dynamicLinkWatchInstances.push(unwatch);
    }

    const introductionTitle = t("dynamic_internal_link_section_activity_dropdown_introduction");
    const isInEditor = parent.richTextContext.pageDomain === "editor";
    let isInEditorMode = route.params.mode === "edit";
    let isInEditorEditMode = isInEditor && isInEditorMode;

    linkTags.forEach((node: Element) => {
      // create dynamic internal link component
      const element = node as HTMLElement;
      const linkObject = DynamicLinkUtils.retrieveDataFromLinkTag(element);
      const linkApp = DynamicLinkUtils.createDynamicInternalLinkVueApp(
        {
          isEditMode: isInEditorEditMode,
          anchor: linkObject.anchor,
          customText: linkObject.customText,
          dataLinkType: linkObject.dataLinkType,
          introductionTitle,
          lessonUuid: parent.richTextContext.lessonUuid ?? "",
        },
        {
          onLoaded: parent.tooltipLoaded,
          onClick: ({ $event, type, id }) => {
            isInEditorMode = route.params.mode === "edit";
            isInEditorEditMode = isInEditor && isInEditorMode;
            if (isInEditorEditMode) {
              //no navigation happens & RichText is for display and hence no edit option is offered.
              return;
            }
            $event.stopPropagation();
            parent.dynamicLinkStore.setNavigationEvent({ type, id: id });
          },
        },
        parent,
      );

      //wrapper
      const wrapper = DynamicLinkUtils.createWrapper(false);
      // wrap link tag with wrapper
      element.parentNode?.insertBefore(wrapper, element);
      wrapper.appendChild(element);
      const component = linkApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(component);
    });
  });
}
function processNotebookDynamicLink(container: HTMLElement, parent) {
  const linkTags = DynamicLinkUtils.getLinkTagsFromContainer(container);
  if (!linkTags?.length) {
    // stop here since no dynamic link is found
    return;
  }
  // executes in parallel, let DynamicInternalLinks update reactively
  fetchNotebookAnchor(parent).then(() => {
    // get new lesson anchor when new activity is added

    const notebookStore = useNotebookEditorStore();
    if (notebookStore.getOrderedNotebookPageStructures()) {
      const pageWatcher = debounceFn(
        function () {
          parent.dynamicLinkStore.refreshNotebookAnchor();
        },
        500,
        { leading: true, trailing: false },
      );
      const unwatch = store.watch(
        () => {
          return notebookStore.getOrderedNotebookPageStructures();
        },
        () => pageWatcher(),
        { deep: true },
      );
      parent.dynamicLinkWatchInstances.push(unwatch);
    }

    const introductionTitle = t("dynamic_internal_link_section_activity_dropdown_introduction");
    const isInEditor = parent.richTextContext.pageDomain === "editor";
    let isInEditorMode = route.params.mode === "edit";
    let isInEditorEditMode = isInEditor && isInEditorMode;

    linkTags.forEach((node: Element) => {
      // create dynamic internal link component
      const element = node as HTMLElement;
      const linkObject = DynamicLinkUtils.retrieveDataFromLinkTag(element);
      const linkApp = DynamicLinkUtils.createDynamicInternalLinkVueApp(
        {
          isEditMode: isInEditorEditMode,
          anchor: linkObject.anchor,
          customText: linkObject.customText,
          dataLinkType: linkObject.dataLinkType,
          introductionTitle,
        },
        {
          onLoaded: parent.tooltipLoaded,
          onClick: ({ $event, type, id }) => {
            isInEditorMode = route.params.mode === "edit";
            isInEditorEditMode = isInEditor && isInEditorMode;
            if (isInEditorEditMode) {
              //no navigation happens & RichText is for display and hence no edit option is offered.
              return;
            }
            $event.stopPropagation();
            parent.dynamicLinkStore.setNavigationEvent({ type, id: id });
          },
        },
        parent,
      );

      //wrapper
      const wrapper = DynamicLinkUtils.createWrapper(false);
      // wrap link tag with wrapper
      element.parentNode?.insertBefore(wrapper, element);
      wrapper.appendChild(element);
      const component = linkApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(component);
    });
  });
}

function processParagraph(el: HTMLElement) {
  RichTextUtils.processToPreserveLineBreaks(el);
}

function updateIsExpandable() {
  if (isDestroyed.value) return;
  if (!props.visible) return;
  if (isExpandable.value && isExpanded.value) return;
  nextTick(() => {
    //in the case when richtext is programically expanded(annotation anchoring)
    //isExpanded will be true causing the checking of readless/readmore to be inaccurate.
    if (isExpandable.value && isExpanded.value) return;
    calculateContentHeight();
    const el = containerRef.value;
    if (DOMUtils.elOf(el, "figure.media").length) {
      if (!props.truncate) {
        // for MO not in Comment Section, youtube and embed websites, expand by default if not fixed height
        if (isExpandable.value && !props.isComment) {
          isExpanded.value = true;
        }
      }
    }
  });
}

function calculateContentHeight() {
  const el = containerRef.value;

  // container.clientHeight returns few more pixel than actual height,
  // and need to add some extra height to correct the calculation
  const clientHeight = el.clientHeight + EXTRA_HEIGHT;

  //SLS-12437 For testing, to remove
  console.log({
    innerHeight: window.innerHeight,
    threshold50: window.innerHeight * 0.5,
    clientHeight: el.clientHeight,
    scrollHeight: el.scrollHeight,
    calculatedClientHeight: clientHeight,
  });

  isExpandable.value = clientHeight < el.scrollHeight;
  // Set the desired initial expanded state only after expandability calculation.
  isExpanded.value = props.expanded ?? false;
  isCheckingExpandability.value = false;
}

function handleToggle() {
  isExpanded.value = !isExpanded.value;
}

function tooltipLoaded(id: string) {
  updatePluginReadyState(id);
}

function algebraKitLoaded(id: string) {
  updatePluginReadyState(id);
}

function mediaLoaded(loadedMediaUuid: string) {
  updatePluginReadyState(loadedMediaUuid);
}

function mediaLoadedLimited(identifier: string) {
  updatePluginLoadedLimitedState(identifier);
}

function clickImage(params: unknown) {
  emit("clickImage", params);
}

function timestampTrigger(params: unknown) {
  emit("timestampTrigger", params);
}

function isModeChanged(params: unknown) {
  emit("isModeChanged", params);
}

function handleMediaSettings(settings: unknown) {
  emit("mediaSettings", settings);
}

function openSettings(settings: Record<string, unknown>) {
  emit("editEmbed", settings);
}

function openCanvasDrawing(params) {
  const canvasConfig: CanvasConfig = {};
  const eventBusWatchInstancesLocal: Unwatch[] = [];
  eventBusWatchInstancesLocal.push(drawingEventBus.$on("commitDrawing", handleCommitDrawing));
  canvasConfig.eventBusWatchInstances = eventBusWatchInstancesLocal;
  if (params && params.uuid) {
    // basic image information
    canvasConfig.imageUrl = params.imageUrl;
    canvasConfig.uuid = params.uuid;
    canvasConfig.fileName = params.fileName;
    canvasConfig.createAsNew = !props.isComment;
    canvasConfig.skipCropping = params.skipCropping;
  }
  // @ts-expect-error runtime plugin
  drawModalId.value = modal.show(DrawingCanvasModal, canvasConfig, {}, true);
}

function handleCommitDrawing(drawingData: unknown) {
  // @ts-expect-error runtime plugin
  modal.remove(drawModalId.value);
  nextTick(() => {
    emit("markCopy", drawingData);
  });
}

function handleClick($event: MouseEvent) {
  const clickedElement = $event.target as HTMLElement;
  if (props.skeleton || props.loading) {
    return;
  }
  if (DOMUtils.isRichTextWhiteListedClickedElement(clickedElement)) {
    return;
  }
  emit("focus");
}

function addPluginReadyState(pluginName: string) {
  richTextPluginReadyMap.value[pluginName] = false;
  richTextPluginLoadedLimitedMap.value[pluginName] = false;
}

function updatePluginReadyState(pluginName: string) {
  richTextPluginReadyMap.value[pluginName] = true;
  richTextPluginLoadedLimitedMap.value[pluginName] = true;
  checkRichTextReady();
}

function updatePluginLoadedLimitedState(pluginName: string) {
  richTextPluginLoadedLimitedMap.value[pluginName] = true;
  checkRichTextLoadedLimited();
}

function checkRichTextReady() {
  const allReady = !Object.values(richTextPluginReadyMap.value).some((v) => v === false);
  if (allReady && whitelistHtmlFetched.value) {
    richTextReady.value = true;
    richTextLoadedLimited.value = true;
    updateIsExpandable();
    emit("ready");
  }
}

// tracks rich text loaded state regardless of whether media is actually ready (e.g., true if an unpublished MO shows an empty placeholder)
// richTextLoadedLimited is used to determine the `loaded` css class, to cater for LessonViewerActivity allLoaded check
// separate flag from richTextReady to not enable certain features (e.g., annotation)
function checkRichTextLoadedLimited() {
  const allLoaded = !Object.values(richTextPluginLoadedLimitedMap.value).some((v) => v === false);
  if (allLoaded) {
    richTextLoadedLimited.value = true;
  }
}

function isMediaObjectContextAnchor(anchor: string): boolean {
  return /_MO_/.test(anchor);
}

function handleAnchoringToContextMessageMedia(anchor: string) {
  const [richTextId, index] = anchor.split("_MO_");
  const anchoringIndex = `MO_${index}`;

  const richTextElement = DOMUtils.elOf(
    document as unknown as Element,
    `.annotatable-richtext#${richTextId}`,
  )[0] as HTMLElement | undefined;

  if (!richTextElement) {
    return;
  }

  const anchorElement =
    (DOMUtils.elOf(richTextElement, `.media-component#${anchoringIndex}`)[0] as
      HTMLElement | undefined) ?? null;

  handleAnchor({
    anchorElement: anchorElement,
    anchor: anchor,
  });
}

async function handleAnchoringToContextMessageText(contextMessage: ContextMessage) {
  const anchor = contextMessage.anchor.referenceId;

  // pause selection change listeners to prevent AnnotationToolBar from showing up
  const rangy = instance?.appContext.config.globalProperties.$rangy;
  const uuid = RandomUtils.randomUuid();
  rangy.pauseSelectionChangeListeners(uuid);

  // clear any pending resume so re-entry within 2s keeps listeners paused
  if (selectionListenersResumeTimeout.value !== null) {
    clearTimeout(selectionListenersResumeTimeout.value);
    selectionListenersResumeTimeout.value = null;
  }

  const containerEl = document.getElementById(anchor);
  if (!rangy || !containerEl) return;

  // create range at the exact character positions
  const range = rangy.createRange();
  range.selectCharacters(
    containerEl,
    contextMessage.anchor.startIndex,
    contextMessage.anchor.endIndex,
  );

  // insert a temporary marker at the start of the selection to scroll to the exact position
  const nativeRange = range.nativeRange ?? range;
  const marker = document.createElement("span");
  nativeRange.insertNode(marker);

  // scroll to the marker
  await handleAnchor({
    anchorElement: marker,
    anchor: anchor,
  });

  // clean up: remove marker and normalize split text nodes
  marker.remove();
  containerEl.normalize();

  // highlight context in lesson viewer activity
  const freshRange = rangy.createRange();
  freshRange.selectCharacters(
    containerEl,
    contextMessage.anchor.startIndex,
    contextMessage.anchor.endIndex,
  );
  const sel = rangy.getSelection();
  sel.removeAllRanges();
  sel.setSingleRange(freshRange);

  // resume selection change listeners after 2s; resets if called again within that window
  selectionListenersResumeTimeout.value = setTimeout(() => {
    selectionListenersResumeTimeout.value = null;
    rangy.resumeSelectionChangeListeners(uuid);
  }, 2000);
}

async function handleAnchoringToContextMessage(contextMessage: ContextMessage) {
  const anchor = contextMessage.anchor.referenceId;

  if (!anchor.includes(props.anchor)) {
    return;
  }

  if (!isExpanded.value) {
    handleToggle();
  }

  const isMediaObjectAnchor = isMediaObjectContextAnchor(anchor);

  if (isMediaObjectAnchor) {
    handleAnchoringToContextMessageMedia(anchor);
  } else {
    handleAnchoringToContextMessageText(contextMessage);
  }
}

function handleAnchoringToAnnotation(annotation: Annotation) {
  if (!annotation.anchor.includes(props.anchor)) {
    return;
  }

  const docEl = document as unknown as Element;
  nextTick(() => {
    let annotationElement: HTMLElement | undefined;
    if (annotation.outdated) {
      // Trim and remove the MO in anchor to get the container IDs
      const anchor = annotation.anchor.includes("_MO")
        ? annotation.anchor.split("_MO")[0]
        : annotation.anchor;
      if (!annotation.anchor.startsWith("RT") && !annotation.anchor.match(/_(RT)_\d+/g)) {
        // For question components, anchor on question container including header label
        const questionBody = DOMUtils.elOf(docEl, `[id="${anchor}"]`)[0] as HTMLElement;
        annotationElement = DOMUtils.parent(questionBody, ".question-container") as HTMLElement;
      } else {
        // For richtext components, it doesn't have a header label
        annotationElement = DOMUtils.elOf(docEl, `[id="${anchor}"]`)[0] as HTMLElement;
      }
    } else {
      if (annotation.type === ANNOTATION_TYPE.TOOLTIP_ANNOTATION) {
        const anchoringIndex = annotation.anchor.substring(annotation.anchor.indexOf("TOOLTIP_"));
        annotationElement = DOMUtils.elOf(
          docEl,
          `#component-${annotation.annotationResourceId}  [index="${anchoringIndex}"]`,
        )[0] as HTMLElement;
      } else if (
        annotation.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION ||
        annotation.type === ANNOTATION_TYPE.IMAGE_ANNOTATION
      ) {
        const anchoringIndex = annotation.anchor.substring(annotation.anchor.indexOf("MO_"));
        const richtextEl = DOMUtils.elOf(
          docEl,
          `.annotatable-richtext#${annotation.anchor.split("_MO")[0]}`,
        )[0] as HTMLElement;
        annotationElement = DOMUtils.elOf(
          richtextEl,
          `.media-component#${anchoringIndex}`,
        )[0] as HTMLElement;
        if (annotation.type === ANNOTATION_TYPE.TIMESTAMP_ANNOTATION) {
          const mediaElement = DOMUtils.elOf(annotationElement, `.media`)[0] as HTMLElement;
          const timestamp =
            annotation.data.highlighterSelectors[0].start === 0
              ? 0
              : annotation.data.highlighterSelectors[0].start / 1000;
          MediaPlayerEventBus.$emit("panToPoint", {
            time: timestamp,
            pauseMedia: true,
            uuid: mediaElement.getAttribute("data-uuid"),
          });
        }
      } else {
        //when LFA returns highlight that exceed the content character limit, the highlightedContent will be empty, this case should anchor to the store anchor
        if (isEmpty(annotation.data.highlighterId) || isEmpty(annotation.data.highlightedContent)) {
          annotationElement = DOMUtils.elOf(
            docEl,
            `.annotatable-richtext#${annotation.anchor}`,
          )[0] as HTMLElement;
        } else {
          annotationElement = DOMUtils.elOf(
            docEl,
            `[data-id="${annotation.data.highlighterId}"]`,
          )[0] as HTMLElement;
        }
      }
    }

    handleAnchor({
      anchorElement: annotationElement,
      anchor: annotation.anchor,
      latestSnapshot: annotation.latestSnapshot,
    });

    AnnotationEventBus.$emit("annotationCardSelected", annotation);
  });
}

async function handleAnchor({ anchorElement, anchor, latestSnapshot = false }) {
  if (!anchorElement) {
    return;
  }

  const scrollContainer = document.scrollingElement as Element & { scrollTop: number };

  let additionalOffset = 0;
  if (!annotationContext.isOwnView) {
    //consider viewing other person's header
    additionalOffset = ANCHOR_ADDITIONAL_OFFSET.OTHER_VIEW_BAR;
  } else if (annotationContext.isOwnView && annotationContext.isPreview) {
    // teacher view as student
    additionalOffset = ANCHOR_ADDITIONAL_OFFSET.PREVIEW_AS_STUDENT_BANNER;
  } else if (annotationContext.isActivityTeamResponse) {
    // when viewing other person header is present, team activity banner is hidden
    //consider height of space bar for team activity banner in student attempt space
    additionalOffset = ANCHOR_ADDITIONAL_OFFSET.TEAM_RESPONSE_BANNER;
  } else if (annotationContext.isLinkedAssignment() && annotationContext.isTeacher) {
    additionalOffset = ANCHOR_ADDITIONAL_OFFSET.LINKED_ASSIGNMENT_BANNER;
  }

  if (annotationContext.isAssignmentPaused()) {
    additionalOffset = additionalOffset + ANCHOR_ADDITIONAL_OFFSET.ASSIGNMENT_PAUSE_BANNER;
  }

  if (
    annotationContext.isGameEnabled() &&
    !annotationContext.isTeacher &&
    !annotationContext.isPaginated
  ) {
    additionalOffset = additionalOffset + ANCHOR_ADDITIONAL_OFFSET.GAMIFICATION_ACHIEVEMENT_BAR;
  }

  if (annotationContext.bannerCount) {
    additionalOffset =
      additionalOffset + annotationContext.bannerCount * ANCHOR_ADDITIONAL_OFFSET.EDITOR_BANNER;
  }

  const headerOffset = 48;
  let totalAdditionalOffset = additionalOffset;

  if (annotationContext.isPaginated) {
    // cater for sticky page navigator overlaying the top of the viewport
    totalAdditionalOffset = totalAdditionalOffset + ANCHOR_ADDITIONAL_OFFSET.STICKY_PAGE_NAVIGATOR;
  }

  // align the anchor within its scrollable container first so only the window scroll is visible
  scrollAnchorContainerToAnchor(anchorElement, anchor, latestSnapshot);

  const getTargetScrollTop = () =>
    anchorElement.getBoundingClientRect().top +
    scrollContainer.scrollTop -
    headerOffset -
    totalAdditionalOffset;

  const targetScrollTopValue = getTargetScrollTop();
  scrollContainer.scrollTo({
    top: targetScrollTopValue,
    left: 0,
    behavior: "smooth",
  });

  await ScrollUtils.waitForScrollEnd(scrollContainer);
  if (!anchorElement.isConnected) {
    return;
  }
  const correctedScrollTopValue = getTargetScrollTop();
  if (Math.abs(correctedScrollTopValue - targetScrollTopValue) > 4) {
    scrollContainer.scrollTo({
      top: correctedScrollTopValue,
      left: 0,
      behavior: "smooth",
    });
  }
}

function scrollAnchorContainerToAnchor(
  anchorElement: HTMLElement,
  anchor: string,
  latestSnapshot: boolean,
) {
  let containerElement: HTMLElement | undefined;
  if (anchor.includes("SAS") && latestSnapshot) {
    containerElement = DOMUtils.elOf(instance.proxy?.$el, ".rich-text-content")[0] as HTMLElement;
  } else if (anchor.includes("SA")) {
    containerElement = document.getElementById(anchor)?.parentElement ?? undefined;
  }

  if (!containerElement) {
    return;
  }

  const offset = anchor.includes("SA") ? -48 : 0;
  ScrollUtils.scrollToTopOfContainerWithHeaderOffset(anchorElement, containerElement, offset);
}

function handleAnchorAnnotation(index: string) {
  emit("handleAnchorAnnotation", index);
}

function handleMakeImageCopy() {
  emit("handleMakeImageCopy");
}

function runOrQueue(task: () => void) {
  if (parentExposed.value) {
    task();
  } else {
    pendingTasks.push(task);
  }
}

function processContent() {
  const parent = parentExposed.value;
  if (!parent) {
    runOrQueue(processContent);
    return;
  }
  const el = containerRef.value as HTMLElement;
  //NOTE: plugin that calls api should be processed 1st,
  //in case, some plugins is set ready before all the plugins could be set to be false
  processSlsLabel(el, parent);
  processDynamicLink(el, parent);
  processMediaObjects(el, parent);
  processGoogleAttachments(el, parent);
  processWiris(el, parent);
  processTooltips(el, parent);
  processStpTooltips(el, parent);
  processStudentTooltips(el, parent);
  processYouTubes(el, parent);
  processSpeechEvaluation(el, parent);
  processAlgebraKit(el, parent);
  processAlgebraKitAnnotation(el, parent);
  processEmbedWebsite(el, parent);
  processTable(el);
  processMention();
  processParagraph(el);
}

const { trackWatch } = useEventBusWatch();
const { dynamicMountParentMixin_childComponents } = useDynamicMountParent();

watch(
  () => props.content,
  () => {
    nextTick(() => {
      processContent();
      if (!richTextReady.value) return;
      updateIsExpandable();
    });
  },
);

watch(
  () => props.visible,
  () => {
    // Expandability calculation and processing of content must be done after:
    // 1. Richtext is displayed to user
    // 2. All media is loaded
    if (!props.visible) return;
    processContent();
    if (!richTextReady.value) return;
    updateIsExpandable();
  },
);

watch(
  () => props.loading,
  () => {
    // when richtext finish loading and content is once again rendered
    // process medias inside richtext and update expandable
    if (props.loading) return;
    nextTick(() => {
      processContent();
      if (!richTextReady.value) return;
      updateIsExpandable();
    });
  },
);

watch(
  () => props.timeStamp,
  () => {
    if (!props.visible) return;
    richTextMedias.value.forEach((m) => (m.timeStamp = props.timeStamp));
    if (!richTextReady.value) return;
    if (isExpandable.value && !props.expanded && props.isComment) {
      isExpanded.value = false;
    }
  },
);

watch(
  () => props.expandable,
  (v) => {
    isExpandable.value = v && !!props.content;
    nextTick(() => {
      processContent();
      if (!richTextReady.value) return;
      updateIsExpandable();
    });
  },
);

onMounted(async () => {
  instance = getCurrentInstance();
  if (instance?.exposed) {
    parentExposed.value = instance.exposed as Record<string, unknown>;
  }
  if (!props.skeleton && !props.loading) {
    await storeVuex.dispatch(`${NAMESPACE.APP}/fetchWhitelistHtmlTags`);
    whitelistHtmlFetched.value = true;
    processContent();

    if (props.enableAnnotationScroll) {
      trackWatch(
        AnchoringEventBus.$on("scrollToView", (params) => {
          if (params.contextType === ANCHORING_CONTEXT_TYPE.ANNOTATION) {
            const annotation = params.data;
            let hasAnnotation: Element | null;
            if (!instance) return;
            if (annotation.type !== ANNOTATION_TYPE.TOOLTIP_ANNOTATION) {
              hasAnnotation = instance!.proxy?.$el.querySelector(
                `[data-annotation-uuid="${annotation.uuid}"]`,
              );
            } else {
              const anchoringIndex = annotation.anchor.substring(
                annotation.anchor.indexOf("TOOLTIP_"),
              );
              hasAnnotation = instance!.proxy?.$el.querySelector(`[index="${anchoringIndex}"]`);
            }
            if (hasAnnotation && !isExpanded.value) {
              handleToggle();
            }
            handleAnchoringToAnnotation(annotation);
          } else if (params.contextType === ANCHORING_CONTEXT_TYPE.CONVERSATION_CONTEXT) {
            handleAnchoringToContextMessage(params.data);
          }
        }),
      );
    }
    while (pendingTasks.length) {
      const task = pendingTasks.shift();
      if (task) {
        task();
      }
    }
  }
});

onUnmounted(() => {
  isDestroyed.value = true;
  if (selectionListenersResumeTimeout.value !== null) {
    clearTimeout(selectionListenersResumeTimeout.value);
    selectionListenersResumeTimeout.value = null;
  }
  if (WirisInterval.value) clearInterval(WirisInterval.value);
  dynamicLinkWatchInstances.value.forEach((unwatch) => {
    if (unwatch) {
      unwatch();
    }
  });
});

defineExpose({
  richTextContext,
  annotationContext,
  questionContext,
  pageContext,
  questionAttemptContext,
  resourceContext,
  componentContext,
  dynamicMountParentMixin_childComponents,
  lazyload: props.lazyload,
  printView,
  config: props.config,
  copyrightProtected: props.copyrightProtected,
  imageRescale: props.imageRescale,
  annotatable: props.annotatable,
  canMarkCopy: props.canMarkCopy,
  isComment: props.isComment,
  hasSEAudio: props.hasSEAudio,
  isSEGraded: props.isSEGraded,
  questionAttemptUuid: props.questionAttemptUuid,
  questionAttemptUuids: props.questionAttemptUuids,
  componentType: props.componentType,
  pageId: props.pageId,
  isOwnView: props.isOwnView,
  isStudentView: props.isStudentView,
  commentContext: props.commentContext,
  showDrawingRestriction: props.showDrawingRestriction,
  showMediaTrash: props.showMediaTrash,
  disableMedia: props.disableMedia,
  showMediaSettings: props.showMediaSettings,
  showMediaDownload: props.showMediaDownload,
  dynamicLinkStore,
  googleDocumentStore,
  addPluginReadyState,
  updatePluginReadyState,
  checkRichTextReady,
  mediaLoaded,
  mediaLoadedLimited,
  tooltipLoaded,
  algebraKitLoaded,
  clickImage,
  isModeChanged,
  handleMediaSettings,
  openSettings,
  openCanvasDrawing,
  timestampTrigger,
  handleAnchorAnnotation,
  get WirisInterval() {
    return WirisInterval.value;
  },
  set WirisInterval(value) {
    return (WirisInterval.value = value);
  },
  get googleAttachments() {
    return googleAttachments.value;
  },
  get whitelistHtmlFetched() {
    return whitelistHtmlFetched.value;
  },
  get richTextMedias() {
    return richTextMedias.value;
  },
  get dynamicLinkWatchInstances() {
    return dynamicLinkWatchInstances.value;
  },
});
</script>
